import { Bars2Icon } from "@heroicons/react/24/solid";
import {
  animate,
  DragControls,
  motion,
  MotionValue,
  Reorder,
  useDragControls,
  useMotionValue
} from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import { Icon } from "../../../components";

interface DataProps {
  id: number;
  content?: ReactNode;
  introContent?: ReactNode;
}
interface AnimatedDraggableListsProps {
  data: DataProps[];
  classNameContent?: string;
}

export const AnimatedDraggableLists: React.FC<AnimatedDraggableListsProps> = ({
  data,
  classNameContent
}) => {
  const [items, setItems] = useState<DataProps[]>(data);
  return (
    <Reorder.Group
      axis="y"
      onReorder={setItems}
      values={items}
      className="flex flex-col gap-4"
    >
      {items?.map((item) => (
        <Item key={item.id} item={item} classNameContent={classNameContent} />
      ))}
    </Reorder.Group>
  );
};

interface ItemProps {
  item: DataProps;
  children?: ReactNode;
  classNameContent?: string;
}

const Item = ({ item, children, classNameContent }: ItemProps) => {
  const y = useMotionValue(0);
  const boxShadow = useRaisedShadow(y);
  const dragControls = useDragControls();
  const ID = item.id;

  return (
    <Reorder.Item
      value={item}
      id={ID.toString()}
      style={{ boxShadow, y }}
      dragListener={false}
      dragControls={dragControls}
      className={`${
        classNameContent
          ? classNameContent
          : "flex w-72 shrink-0 items-center justify-between gap-4 rounded-lg bg-opacity-darker p-3"
      }`}
    >
      <Content item={item}>{children}</Content>
      <ReorderIcon dragControls={dragControls} />
    </Reorder.Item>
  );
};

const Content = ({ item }: ItemProps) => (
  <div className="flex-1 select-none">
    {!item.introContent && (
      <>
        <div className="mt-3 h-2 w-full rounded-md bg-gray-dark" />
        <div className="mt-3 h-2 w-full rounded-md bg-gray-dark" />
      </>
    )}
    {item.introContent}
  </div>
);

const useRaisedShadow = (value: MotionValue<number>) => {
  const inactiveShadow = "0px 0px 0px rgba(0,0,0,0.8)";
  const boxShadow = useMotionValue(inactiveShadow);

  useEffect(() => {
    let isActive = false;
    value.onChange((latest) => {
      const wasActive = isActive;
      if (latest !== 0) {
        isActive = true;
        if (isActive !== wasActive) {
          animate(boxShadow, "5px 5px 10px rgba(0,0,0,0.3)");
        }
      } else {
        isActive = false;
        if (isActive !== wasActive) {
          animate(boxShadow, inactiveShadow);
        }
      }
    });
  }, [value, boxShadow]);

  return boxShadow;
};

interface Props {
  dragControls: DragControls;
}

const ReorderIcon = ({ dragControls }: Props) => (
  <motion.div
    onPointerDown={(event) => dragControls.start(event)}
    whileTap={{ cursor: "grabbing" }}
    className="cursor-grab"
  >
    <Icon Icon={Bars2Icon} classNameIcon="text-black-lightest w-4 h-4" />
  </motion.div>
);
